import React, { useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { CHECKLIST_STATUS_STYLES } from '../../constants';
import ChecklistStatusDot from '../../reportCards/ChecklistStatusDot';
import EmptyTableRow from '../../reportCards/EmptyTableRow';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReadOnlyField from '../../reportCards/ReadOnlyField';
import ReportCard from '../../reportCards/ReportCard';
import ReportNote from '../../reportCards/ReportNote';
import SortTableCell from '../../reportCards/SortTableCell';
import type { ManualApprovalState } from '../../types';

import type { BundledExperienceChecklistRow } from './types';

interface Props {
  hiddenApprovedCount: number;
  manualApprovals: ManualApprovalState;
  rows: Array<BundledExperienceChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
}

export default function BundledExperiencesReportCard({
  hiddenApprovedCount,
  manualApprovals,
  rows,
  showAutoApproved,
  onApprovalChange,
}: Props) {
  const [expandedDeals, setExpandedDeals] = useState<Record<string, boolean>>({});

  return (
    <ReportCard>
      <Stack spacing={1.5}>
        <ReportNote
          icon={<InfoOutlinedIcon />}
          text={
            <>
              <strong>Bundled Experiences:</strong> auto-approve when validity, blackouts, currency, and child-age
              evidence can be checked from synced deal-option and opportunity data.
            </>
          }
        />
        <ReportNote
          icon={<WarningAmberIcon />}
          severity="warning"
          text={
            <>
              <strong>PAX manual check:</strong> all PAX for bundled experiences still need Campaign Manager review.
            </>
          }
        />
      </Stack>

      <TableContainer>
        <Table size="small" sx={{ minWidth: 1320 }}>
          <TableHead>
            <TableRow>
              <SortTableCell>Bundled Experience / Deal Options</SortTableCell>
              <SortTableCell>Validity Date</SortTableCell>
              <SortTableCell>Blackouts</SortTableCell>
              <SortTableCell>Currency</SortTableCell>
              <SortTableCell>Children Age</SortTableCell>
              <SortTableCell>Checklist Status</SortTableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={7} label="No bundled experience rows match this view." />
            ) : (
              rows.map((row) => {
                const manuallyApproved = !!manualApprovals[row.id];
                const approved = row.status === 'approved' || manuallyApproved;
                const dealsExpanded = !!expandedDeals[row.id];
                const dealCount = row.linkedDealOptions.length;

                return (
                  <React.Fragment key={row.id}>
                    <TableRow hover sx={approved ? CHECKLIST_STATUS_STYLES.approvedRow : undefined}>
                      <TableCell sx={{ minWidth: 320 }}>
                        <ReadOnlyField value={row.bundledExperience} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Stack direction="row" spacing={1}>
                          <ReadOnlyField value={row.travelFrom} />
                          <ReadOnlyField value={row.travelTo} />
                        </Stack>
                        <ChecklistStatusDot status={row.validityOk ? 'approved' : 'requires approval'} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <ReadOnlyField value={row.blackoutDateRange} />
                        <ChecklistStatusDot status={row.blackoutsOk ? 'approved' : 'requires approval'} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <ReadOnlyField value={row.currency} />
                        <ChecklistStatusDot status={row.currencyOk ? 'approved' : 'requires approval'} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 210 }}>
                        <ReadOnlyField value={row.childAge} />
                        <ChecklistStatusDot status={row.childAgeOk ? 'approved' : 'requires approval'} />
                      </TableCell>
                      <TableCell sx={{ minWidth: 170 }}>
                        <ChecklistStatusDot
                          approvalType={manuallyApproved ? 'manual' : 'auto'}
                          status={approved ? 'approved' : row.status}
                        />
                      </TableCell>
                      <TableCell sx={{ minWidth: 160, width: 170 }}>
                        <Stack spacing={0.75} alignItems="stretch">
                          {dealCount > 0 && (
                            <Button
                              fullWidth
                              size="small"
                              variant="text"
                              endIcon={dealsExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              sx={{ justifyContent: 'space-between', whiteSpace: 'nowrap' }}
                              onClick={() => setExpandedDeals((current) => ({ ...current, [row.id]: !dealsExpanded }))}
                            >
                              {getDealToggleLabel({ dealCount, dealsExpanded })}
                            </Button>
                          )}
                          {!approved && (
                            <Button
                              fullWidth
                              size="small"
                              variant="contained"
                              startIcon={<CheckIcon />}
                              sx={{ whiteSpace: 'nowrap' }}
                              onClick={() => onApprovalChange(row.id)}
                            >
                              Approve
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                    {dealCount > 0 && (
                      <TableRow sx={approved ? CHECKLIST_STATUS_STYLES.approvedRow : undefined}>
                        <TableCell colSpan={7} sx={{ p: 0, borderBottom: dealsExpanded ? undefined : 'none' }}>
                          <Collapse in={dealsExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 2, py: 1.5 }}>
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color="text.secondary"
                                sx={{ letterSpacing: '0.08em', display: 'block', mb: 1 }}
                              >
                                LINKED DEAL OPTIONS
                              </Typography>
                              <Stack direction="row" flexWrap="wrap" gap={1}>
                                {row.linkedDealOptions.map((dealOption) => (
                                  <Chip key={dealOption} label={dealOption} size="small" variant="outlined" />
                                ))}
                              </Stack>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <HiddenApprovedFooter
        count={hiddenApprovedCount}
        noun="bundled experience row"
        showAutoApproved={showAutoApproved}
      />
    </ReportCard>
  );
}

function getDealToggleLabel({ dealCount, dealsExpanded }: { dealCount: number; dealsExpanded: boolean }) {
  const dealNoun = dealCount === 1 ? 'deal' : 'deals';
  return `${dealsExpanded ? 'Hide' : 'View'} ${dealCount} ${dealNoun}`;
}
